import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import { Box, Button, Typography, Grid, CircularProgress, Menu, MenuItem, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell, Chip } from '@material-ui/core'
import { ArrowBack } from '@material-ui/icons'
import { TASK_STATUS, TASK_STATUS_LABELS, TASK_STATUS_COLORS } from '../constants/bidStatuses'
import SubScopePanel from './SubScopePanel/SubScopePanel'
import AreaList from './AreaList/AreaList'
import AreaFormModal from './AreaList/AreaFormModal'
import AreaWorkspace from './AreaWorkspace/AreaWorkspace'

const ModuleWorkspace = (props) => {
  const { projectId, moduleId } = useParams()
  const history = useHistory()
  const { url, user } = props

  const [module, setModule] = React.useState(null)
  const [areas, setAreas] = React.useState([])
  const [selectedAreaId, setSelectedAreaId] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [areaModalOpen, setAreaModalOpen] = React.useState(false)
  const [editingArea, setEditingArea] = React.useState(null)
  const [statusAnchor, setStatusAnchor] = React.useState(null)
  const [activeTab, setActiveTab] = React.useState(0)
  const [moduleLineItems, setModuleLineItems] = React.useState([])
  const [moduleProjectServices, setModuleProjectServices] = React.useState([])
  const [allServices, setAllServices] = React.useState([])

  const debounceRef = React.useRef(null)

  const fetchModule = React.useCallback(() => {
    axios.get(`${url}/api/bid-estimation/modules/${moduleId}`).then(res => {
      setModule(res.data)
    }).catch(err => console.error('Failed to fetch module:', err))
  }, [url, moduleId])

  const fetchAreas = React.useCallback(() => {
    axios.get(`${url}/api/bid-estimation/modules/${moduleId}/areas`).then(res => {
      setAreas(res.data)
    }).catch(err => console.error('Failed to fetch areas:', err))
  }, [url, moduleId])

  React.useEffect(() => {
    setLoading(true)
    Promise.all([
      axios.get(`${url}/api/bid-estimation/modules/${moduleId}`),
      axios.get(`${url}/api/bid-estimation/modules/${moduleId}/areas`)
    ]).then(([modRes, areasRes]) => {
      setModule(modRes.data)
      setAreas(areasRes.data)
      setLoading(false)
    }).catch(err => {
      console.error('Failed to load workspace:', err)
      setLoading(false)
    })
  }, [url, moduleId])

  // Fetch line items when Line Items tab is active
  React.useEffect(() => {
    if (activeTab === 1) {
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/line-items`, { params: { moduleId } })
        .then(res => setModuleLineItems(res.data))
        .catch(err => console.error('Failed to fetch module line items:', err))
    }
  }, [url, moduleId, activeTab])

  // Fetch project services and service catalog when Services tab is active
  React.useEffect(() => {
    if (activeTab === 2) {
      axios.get(`${url}/api/bid-estimation/projects/${projectId}/project-services`).then(res => {
        const moduleCat = (module?.scope || '').toLowerCase()
        setModuleProjectServices((res.data || []).filter(ps => ps.serviceCategory === moduleCat))
      }).catch(() => {})
      axios.get(`${url}/api/bid-estimation/services`).then(res => {
        setAllServices(res.data || [])
      }).catch(() => {})
    }
  }, [url, projectId, moduleId, activeTab, module])

  // ── Sub-scope handlers ───────────────────────────────────────

  const saveSubScopes = (updatedScopes) => {
    setSaving(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      axios.put(`${url}/api/bid-estimation/modules/${moduleId}/sub-scopes`, { subScopes: updatedScopes })
        .then(res => { setModule(res.data); setSaving(false) })
        .catch(err => { console.error('Sub-scope save failed:', err); setSaving(false) })
    }, 600)
  }

  const handleToggleSubScope = (key) => {
    const updated = (module.subScopes || []).map(s =>
      s.key === key ? { ...s, selected: !s.selected } : s
    )
    setModule(prev => ({ ...prev, subScopes: updated }))
    saveSubScopes(updated)
  }

  const handleAddCustomSubScope = (label) => {
    axios.post(`${url}/api/bid-estimation/modules/${moduleId}/sub-scopes`, { label })
      .then(res => setModule(res.data))
      .catch(err => console.error('Add custom failed:', err))
  }

  const handleRemoveCustomSubScope = (key) => {
    axios.delete(`${url}/api/bid-estimation/modules/${moduleId}/sub-scopes/${key}`)
      .then(res => setModule(res.data))
      .catch(err => console.error('Remove custom failed:', err))
  }

  // ── Area handlers ────────────────────────────────────────────

  const handleAreaSave = (data) => {
    const request = editingArea
      ? axios.put(`${url}/api/bid-estimation/areas/${editingArea._id}`, data)
      : axios.post(`${url}/api/bid-estimation/modules/${moduleId}/areas`, data)

    request.then(() => {
      setAreaModalOpen(false)
      setEditingArea(null)
      fetchAreas()
    }).catch(err => console.error('Area save failed:', err))
  }

  const handleAreaDelete = (area) => {
    if (!window.confirm(`Delete area "${area.name}"?`)) return
    axios.delete(`${url}/api/bid-estimation/areas/${area._id}`).then(() => {
      if (selectedAreaId === area._id) setSelectedAreaId(null)
      fetchAreas()
    }).catch(err => console.error('Area delete failed:', err))
  }

  // ── Clarification status ─────────────────────────────────────

  const handleStatusChange = (newStatus) => {
    setStatusAnchor(null)
    axios.put(`${url}/api/bid-estimation/modules/${moduleId}/clarification-status`, { status: newStatus })
      .then(res => setModule(res.data))
      .catch(err => console.error('Status update failed:', err))
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: '#fafafa', minHeight: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
        <CircularProgress />
      </div>
    )
  }

  const clarStatus = module?.clarificationStatus || 'not_started'

  return (
    <div style={{ backgroundColor: '#fafafa', minHeight: '100%' }}>
      {/* Header */}
      <Box style={{
        backgroundColor: '#2e7d32', color: '#fff',
        padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Button size="small" startIcon={<ArrowBack />}
            onClick={() => history.push(`/bid-estimation/${projectId}`)}
            style={{ color: '#fff', textTransform: 'none' }}>
            Back
          </Button>
          <Typography variant="h6" style={{ fontWeight: 600, color: '#fff' }}>
            {module?.scope || 'Module'}
          </Typography>
        </div>
        <span
          onClick={(e) => setStatusAnchor(e.currentTarget)}
          style={{
            backgroundColor: TASK_STATUS_COLORS[clarStatus] || '#9e9e9e',
            color: '#fff', padding: '4px 12px', borderRadius: 12,
            fontSize: 12, cursor: 'pointer'
          }}
        >
          {TASK_STATUS_LABELS[clarStatus] || clarStatus}
        </span>
        <Menu anchorEl={statusAnchor} open={!!statusAnchor} onClose={() => setStatusAnchor(null)}>
          {Object.values(TASK_STATUS).map(s => (
            <MenuItem key={s} onClick={() => handleStatusChange(s)} style={{ fontSize: 12 }}>
              {TASK_STATUS_LABELS[s]}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Tab Bar */}
      <div style={{ padding: '0 20px' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="Clarification" />
          <Tab label="Line Items" />
          <Tab label="Services" />
        </Tabs>
      </div>

      {/* Tab Content */}
      <div style={{ padding: 20 }}>
        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SubScopePanel
                subScopes={module?.subScopes || []}
                onToggle={handleToggleSubScope}
                onAddCustom={handleAddCustomSubScope}
                onRemoveCustom={handleRemoveCustomSubScope}
                saving={saving}
              />
              <AreaList
                areas={areas}
                selectedAreaId={selectedAreaId}
                onSelect={setSelectedAreaId}
                onAdd={() => { setEditingArea(null); setAreaModalOpen(true) }}
                onEdit={(area) => { setEditingArea(area); setAreaModalOpen(true) }}
                onDelete={handleAreaDelete}
              />
            </Grid>
            <Grid item xs={12} md={9}>
              <AreaWorkspace areaId={selectedAreaId} url={url} user={user} />
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <div style={{ backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Line Items — {module?.scope || 'Module'}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => history.push(`/bid-estimation/${projectId}/items?moduleId=${moduleId}`)}
                style={{ textTransform: 'none', fontSize: 12 }}
              >
                Go to Line Items
              </Button>
            </div>
            {moduleLineItems.length === 0 ? (
              <Typography variant="body2" color="textSecondary">No line items assigned to this module yet.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Total Sell</TableCell>
                    <TableCell>Coverage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {moduleLineItems.map(item => (
                    <TableRow key={item._id}>
                      <TableCell>{item.itemNo}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">${(item.totalSell || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <Chip size="small" label={item.coverageStatus || 'missing'}
                          style={{ fontSize: 10, height: 20,
                            backgroundColor: item.coverageStatus === 'covered' ? '#e8f5e9' : item.coverageStatus === 'missing' ? '#ffebee' : '#fff8e1'
                          }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {activeTab === 2 && (
          <div style={{ backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                Services — {module?.scope || 'Module'}
              </Typography>
              <Button size="small" variant="outlined"
                onClick={() => history.push(`/bid-estimation/${projectId}/estimating`)}
                style={{ textTransform: 'none', fontSize: 12 }}>
                Open in Cockpit →
              </Button>
            </div>
            {allServices
              .filter(s => s.category === (module?.scope || '').toLowerCase())
              .map(svc => {
                const ps = moduleProjectServices.find(p => p.serviceId === svc.serviceId)
                const isActive = !!ps
                return (
                  <div key={svc.serviceId} style={{
                    display: 'flex', alignItems: 'center', padding: 8, gap: 10,
                    border: `1px solid ${isActive ? '#e8f5e9' : '#ddd'}`, borderRadius: 6,
                    marginBottom: 6, backgroundColor: isActive ? '#f1f8e9' : '#fff', cursor: 'pointer'
                  }}>
                    <span style={{
                      width: 18, height: 18, border: `2px solid ${isActive ? '#4caf50' : '#ddd'}`,
                      borderRadius: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: isActive ? '#4caf50' : 'transparent', color: '#fff', fontSize: 11
                    }}>
                      {isActive ? '✓' : ''}
                    </span>
                    <div>
                      <div style={{ fontWeight: isActive ? 600 : 500 }}>{svc.name}</div>
                      <div style={{ fontSize: 10, color: isActive ? '#666' : '#999' }}>
                        {isActive ? `${(ps.areas || []).length} areas · ${ps.totalQuantity || 0} ${ps.unit || ''}` : 'Not selected'}
                      </div>
                    </div>
                    {isActive && (
                      <span style={{ marginLeft: 'auto', color: '#2e7d32', fontWeight: 600, fontSize: 12 }}>
                        ${(ps.totalSell || 0).toLocaleString()}
                      </span>
                    )}
                  </div>
                )
              })}
          </div>
        )}
      </div>

      <AreaFormModal
        open={areaModalOpen}
        area={editingArea}
        onClose={() => { setAreaModalOpen(false); setEditingArea(null) }}
        onSave={handleAreaSave}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(ModuleWorkspace)
